import styled from 'styled-components';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from '@hooks';
import { useRouter } from 'next/router';

import { useFilter } from '@context/FilterContext';
import { Caption2 } from '@components/styles/Typography';

const PriceRangeSlider = ({ min, max }) => {
    const { currency } = useTranslation();
    const {
        priceMin,
        priceMax,
        onMinPriceChange,
        onMaxPriceChange,
    } = useFilter();

    const range = useRef(null);
    const router = useRouter();

    useEffect(() => {
        if (router.query.priceMin) {
            onMinPriceChange(router.query.priceMin);
        }
        if (router.query.priceMax) {
            onMaxPriceChange(router.query.priceMax);
        }
    }, [router.query]);

    // Convert to percentage
    const getPercent = useCallback(
        (value) => Math.round(((value - min) / (max - min)) * 100),
        [min, max],
    );

    // Set width of the range to decrease from the left side
    useEffect(() => {
        const minPercent = getPercent(priceMin);
        const maxPercent = getPercent(priceMax);

        if (range.current) {
            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [priceMin, getPercent]);

    // Set width of the range to decrease from the right side
    useEffect(() => {
        const minPercent = getPercent(priceMin);
        const maxPercent = getPercent(priceMax);

        if (range.current) {
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [priceMax, getPercent]);

    return (
        <Container>
            <ThumbLeft
                type="range"
                min={min}
                max={max}
                value={priceMin}
                onChange={(event) => {
                    const value = Math.min(
                        Number(event.target.value),
                        priceMax - 1,
                    );
                    onMinPriceChange(value);
                }}
                style={{ zIndex: priceMin > max - 100 && '5' }}
            />
            <ThumbLeft
                type="range"
                min={min}
                max={max}
                value={priceMax}
                onChange={(event) => {
                    const value = Math.max(
                        Number(event.target.value),
                        priceMin + 1,
                    );
                    onMaxPriceChange(value);
                }}
            />

            <Slider>
                <SliderTrack />
                <SliderRange ref={range} />
                <SliderLeftValue>
                    <Caption2>
                        {priceMin} {currency}
                    </Caption2>
                </SliderLeftValue>
                <SliderRightValue>
                    <Caption2>
                        {priceMax} {currency}
                    </Caption2>
                </SliderRightValue>
            </Slider>
        </Container>
    );
};
const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    height: 50px;
`;
const Slider = styled.div`
    position: relative;
    width: 100%;

    div {
        position: absolute;
    }
`;
const SliderTrack = styled.div`
    border-radius: 3px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.bgGray25};
    width: 100%;
    z-index: 1;
`;
const SliderRange = styled.div`
    background-color: ${({ theme }) => theme.colors.primary};
    z-index: 2;
    height: 2px;
`;
const SliderLeftValue = styled.div`
    color: ${({ theme }) => theme.colors.primary};
    font-size: 12px;
    top: -30px;
    left: -4px;

    @media (max-width: 1024px) {
        left: 0;
    }
`;
const SliderRightValue = styled.div`
    color: ${({ theme }) => theme.colors.primary};
    font-size: 12px;
    top: -30px;
    right: -4px;
    @media (max-width: 1024px) {
        right: 0;
    }
`;

const ThumbLeft = styled.input`
    pointer-events: none;
    position: absolute;
    height: 0;
    width: 100%;
    outline: none;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
    z-index: 9;

    ::-webkit-slider-thumb {
        -webkit-appearance: none;
        -webkit-tap-highlight-color: transparent;
    }

    ::-webkit-slider-thumb {
        background-color: ${({ theme }) => theme.colors.primary};
        border: none;
        border-radius: 50%;
        cursor: pointer;
        height: 10px;
        width: 10px;
        margin-top: 4px;
        pointer-events: all;
        position: relative;
    }

    ::-moz-range-thumb {
        background-color: ${({ theme }) => theme.colors.primary};
        border: none;
        border-radius: 50%;
        cursor: pointer;
        height: 7px;
        width: 7px;
        margin-top: 4px;
        pointer-events: all;
        position: relative;
    }
`;

export default PriceRangeSlider;
